import { useState, useLayoutEffect, useRef, useCallback, type RefCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetNotes,
  useCreateNote,
  useUpdateNote,
  getGetNotesQueryKey,
} from "@workspace/api-client-react";
import type { Notes } from "@workspace/api-client-react";
import {
  Bold, Italic, Underline, Strikethrough,
  AlignLeft, AlignCenter, AlignRight,
  List, ListOrdered, Link as LinkIcon, Highlighter,
  Plus, FileText, ChevronLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { DataMinimisationTip } from "@/components/DataMinimisationTip";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/lib/api-error";

const TITLE_MAX_LENGTH = 100;
const MULTI_NOTE_CREATION_ENABLED =
  import.meta.env.VITE_MULTI_NOTE_CREATION_ENABLED === "true";

// ── helpers ──────────────────────────────────────────────────────────────────

export function wordCount(html: string): number {
  return html
    .replace(/<[^>]*>?/gm, " ")
    .split(/\s+/)
    .filter((w) => w.length > 0).length;
}

function relativeDate(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short" });
}

// ── component ─────────────────────────────────────────────────────────────────

export default function NotesPage() {
  const queryClient = useQueryClient();
  const { data: notes = [], isLoading, refetch } = useGetNotes();
  const createNote = useCreateNote();
  const updateNote = useUpdateNote();
  const { toast } = useToast();

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [mobileView, setMobileView] = useState<"list" | "editor">("list");
  const [title, setTitle] = useState("");
  const [titleError, setTitleError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [wc, setWc] = useState(0);

  const editorRef = useRef<HTMLDivElement | null>(null);

  // Callback ref that only assigns the FIRST mounted instance of the editor
  // div — always the desktop copy (rendered unconditionally in the desktop
  // layout).  When mobileView switches to "editor", a second copy mounts in
  // the mobile layout; we intentionally ignore it so the useLayoutEffect
  // innerHTML writes always target the desktop (visible) element.
  const setEditorRef: RefCallback<HTMLDivElement> = useCallback((el) => {
    if (el !== null && editorRef.current === null) {
      editorRef.current = el;
    }
    // We deliberately ignore null (unmount) calls for the secondary mobile
    // copy.  The desktop copy never unmounts while the page is mounted.
  }, []);
  const titleRef = useRef("");
  const contentRef = useRef("");
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // ── save-generation counter ───────────────────────────────────────────────
  //
  // Incrementing this number invalidates every pending save timer without
  // requiring cancelation of the native timeout.  Every scheduled callback
  // captures the generation at the moment it is queued.  If the counter has
  // advanced by the time the timer fires — because the user switched to
  // another note or created a new one — the callback is a no-op.
  const saveGenRef = useRef(0);

  // ── notes mirror ref ─────────────────────────────────────────────────────
  //
  // Keeps a synchronously updated copy of `notes` so the populate-editor
  // useLayoutEffect can read the latest list without listing `notes` as a
  // dependency.  Background refetches must not re-populate the editor (which
  // would clobber unsaved edits), so the layout effect only runs when
  // selectedId changes.
  const notesRef = useRef<Notes[]>([]);
  notesRef.current = notes;

  // ── auto-select first note once data arrives ──────────────────────────────
  //
  // useLayoutEffect (rather than useEffect) ensures the selection is
  // committed synchronously with the first render that has data, so the
  // populate-editor effect below fires in the same layout pass and the DOM
  // is populated before the browser paints — preventing the
  // blank-editor / stale-word-count flash.
  useLayoutEffect(() => {
    if (!isLoading && notes.length > 0 && selectedId === null) {
      setSelectedId(notes[0].id);
    }
  }, [isLoading, notes, selectedId]);

  // ── populate editor when selection changes ────────────────────────────────
  //
  // useLayoutEffect guarantees editorRef.current is set (DOM committed) when
  // this runs, eliminating the race where useEffect could fire before the
  // editor div was mounted and silently skip the innerHTML write.
  //
  // Dependency array is [selectedId] only.  A background refetch changing
  // `notes` must NOT reinitialise the editor — that would overwrite edits the
  // user is currently making.  notesRef provides fresh data without becoming
  // a dependency.
  useLayoutEffect(() => {
    if (selectedId === null || !editorRef.current) return;
    const note = notesRef.current.find((n) => n.id === selectedId);
    if (!note) return;

    titleRef.current = note.title;
    contentRef.current = note.content;
    setTitle(note.title);
    setTitleError(null);
    setIsSaving(false);
    setWc(wordCount(note.content));
    // Always write innerHTML — including empty string — so switching from a
    // non-empty note to an empty note never leaves old DOM content visible.
    editorRef.current.innerHTML = note.content;
  }, [selectedId]);

  // ── save scheduler ────────────────────────────────────────────────────────
  //
  // All mutable values (noteId, title, content) are captured at schedule time
  // and frozen into the closure.  The delayed callback saves those exact
  // captured values regardless of what the refs hold at fire time.  The
  // generation counter provides an independent abort channel so a stale timer
  // that was not explicitly cleared by cancelTimeout becomes a no-op.
  const scheduleSave = useCallback(() => {
    const capturedId = selectedId;
    if (capturedId === null) return;

    const capturedTitle = titleRef.current.trim();

    if (!capturedTitle) {
      setTitleError("Title cannot be blank.");
      clearTimeout(saveTimeoutRef.current);
      setIsSaving(false);
      return;
    }
    if (capturedTitle.length > TITLE_MAX_LENGTH) {
      setTitleError(`Title must be ${TITLE_MAX_LENGTH} characters or fewer.`);
      clearTimeout(saveTimeoutRef.current);
      setIsSaving(false);
      return;
    }

    const capturedContent = contentRef.current;
    setTitleError(null);
    setIsSaving(true);
    clearTimeout(saveTimeoutRef.current);

    const gen = ++saveGenRef.current;

    saveTimeoutRef.current = setTimeout(() => {
      if (gen !== saveGenRef.current) return;

      updateNote.mutate(
        {
          noteId: capturedId,
          data: { title: capturedTitle, content: capturedContent },
        },
        {
          onSuccess: () => {
            setIsSaving(false);
            refetch();
          },
          onError: (err: unknown) => {
            setIsSaving(false);
            toast({
              title: "Couldn't save your note",
              description: getApiErrorMessage(
                err,
                "We'll keep what you've typed — please try again in a moment.",
              ),
              variant: "destructive",
            });
          },
        },
      );
    }, 1000);
  }, [selectedId, updateNote, refetch, toast]);

  // ── handlers ──────────────────────────────────────────────────────────────

  const handleSelectNote = (note: Notes) => {
    if (note.id === selectedId) return;
    // Cancel any pending save for the departing note — both by clearing the
    // native timer and by advancing the generation counter so any timer that
    // already fired (but hasn't run its callback yet) is also neutralised.
    clearTimeout(saveTimeoutRef.current);
    saveGenRef.current++;
    setSelectedId(note.id);
    setMobileView("editor");
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    titleRef.current = e.target.value;
    setTitle(e.target.value);
    scheduleSave();
  };

  const handleInput = () => {
    if (!editorRef.current) return;
    const html = editorRef.current.innerHTML;
    contentRef.current = html;
    setWc(wordCount(html));
    scheduleSave();
  };

  const execCommand = (cmd: string, val?: string) => {
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
    handleInput();
  };

  const handleNewNote = () => {
    // Cancel pending save for the current note before leaving it.
    clearTimeout(saveTimeoutRef.current);
    saveGenRef.current++;

    createNote.mutate(undefined, {
      onSuccess: (created) => {
        // 1. Write the created note into the TanStack Query cache immediately.
        //    This guarantees notesRef contains the new note *before*
        //    selectedId changes, so the populate-editor useLayoutEffect finds
        //    it and sets innerHTML = "" without racing against a refetch.
        queryClient.setQueryData<Notes[]>(
          getGetNotesQueryKey(),
          (old) => [created, ...(old ?? [])],
        );

        // 2. Transition to the new note.  React 18 batches these updates into
        //    one render, after which the layout effect fires with
        //    selectedId = created.id and notesRef already containing the note.
        setSelectedId(created.id);
        setMobileView("editor");

        // 3. Background revalidation — replaces the optimistic cache entry
        //    with the server-canonical list without blocking the selection.
        void queryClient.invalidateQueries({ queryKey: getGetNotesQueryKey() });

        // 4. Focus title input for immediate rename.
        setTimeout(() => {
          document
            .querySelector<HTMLInputElement>('[data-testid="note-title-input"]')
            ?.select();
        }, 50);
      },
      onError: (err: unknown) => {
        toast({
          title: "Couldn't create note",
          description: getApiErrorMessage(err, "Please try again."),
          variant: "destructive",
        });
      },
    });
  };

  const selectedNote = notes.find((n) => n.id === selectedId) ?? null;

  // ── sidebar ───────────────────────────────────────────────────────────────

  const sidebar = (
    <aside
      className="flex flex-col h-full border-r bg-gray-50 w-full md:w-64 shrink-0"
      data-testid="notes-sidebar"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b bg-white">
        <h1 className="text-base font-semibold text-gray-900" data-testid="page-heading">
          My Notes
        </h1>
        {MULTI_NOTE_CREATION_ENABLED && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            title="New note"
            disabled={createNote.isPending}
            onClick={handleNewNote}
            data-testid="new-note-button"
          >
            <Plus className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="p-4 text-sm text-gray-400">Loading…</div>
        ) : notes.length === 0 ? (
          <div className="p-4 text-sm text-gray-400" data-testid="empty-state">
            No notes yet.
          </div>
        ) : (
          <ul data-testid="note-list">
            {notes.map((note) => (
              <li key={note.id}>
                <button
                  type="button"
                  onClick={() => handleSelectNote(note)}
                  data-testid={`note-item-${note.id}`}
                  className={[
                    "w-full text-left px-4 py-3 border-b transition-colors",
                    "hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary",
                    note.id === selectedId
                      ? "bg-primary/10 border-l-2 border-l-primary"
                      : "border-l-2 border-l-transparent",
                  ].join(" ")}
                >
                  <div className="flex items-start gap-2">
                    <FileText className="h-3.5 w-3.5 mt-0.5 shrink-0 text-gray-400" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">
                        {note.title}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {relativeDate(note.updatedAt)}
                      </p>
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  );

  // ── editor panel ──────────────────────────────────────────────────────────

  const editorPanel = (
    <div className="flex flex-col flex-1 min-w-0 h-full bg-white">
      <div className="md:hidden border-b px-4 py-2">
        <Button
          variant="ghost"
          size="sm"
          className="gap-1 -ml-1 text-gray-600"
          onClick={() => setMobileView("list")}
        >
          <ChevronLeft className="h-4 w-4" />
          All notes
        </Button>
      </div>

      {selectedNote ? (
        <>
          <div className="border-b px-6 py-4 flex items-center justify-between gap-4 bg-white sticky top-0 z-10">
            <div className="flex flex-col min-w-0 flex-1">
              <input
                type="text"
                value={title}
                onChange={handleTitleChange}
                disabled={isLoading}
                maxLength={110}
                placeholder="Note title"
                aria-label="Note title"
                data-testid="note-title-input"
                className="text-2xl font-bold text-gray-900 bg-transparent border-0 outline-none w-full focus:ring-0 p-0 placeholder:text-gray-400 disabled:opacity-50"
              />
              {titleError && (
                <p
                  className="text-sm text-red-600 mt-0.5"
                  role="alert"
                  data-testid="title-error"
                >
                  {titleError}
                </p>
              )}
            </div>
            <div
              className="text-sm text-gray-500 shrink-0"
              data-testid="save-status"
            >
              {isSaving ? "Saving…" : "All changes saved"}
            </div>
          </div>

          <div className="flex flex-col flex-1 max-w-5xl mx-auto w-full p-4 sm:p-6">
            <DataMinimisationTip
              className="mb-3"
              text="Tip: this is your private scratchpad — avoid pasting student personal information you don't need to keep."
            />
            <div className="bg-white border rounded-xl shadow-sm flex flex-col flex-1 overflow-hidden">
              <div className="bg-gray-50 border-b p-2 flex flex-wrap items-center gap-1">
                <select
                  className="h-8 border-gray-300 rounded text-sm bg-white px-2 focus:ring-primary focus:border-primary"
                  onChange={(e) => execCommand("formatBlock", e.target.value)}
                >
                  <option value="P">Normal</option>
                  <option value="H1">Heading 1</option>
                  <option value="H2">Heading 2</option>
                  <option value="H3">Heading 3</option>
                </select>

                <Separator orientation="vertical" className="h-6 mx-1" />

                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => execCommand("bold")} title="Bold" data-write-action>
                  <Bold className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => execCommand("italic")} title="Italic" data-write-action>
                  <Italic className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => execCommand("underline")} title="Underline" data-write-action>
                  <Underline className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => execCommand("strikethrough")} title="Strikethrough" data-write-action>
                  <Strikethrough className="h-4 w-4" />
                </Button>

                <Separator orientation="vertical" className="h-6 mx-1" />

                <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600" onClick={() => execCommand("foreColor", "#1a73e8")} title="Text Color" data-write-action>
                  <div className="w-4 h-4 bg-primary rounded-sm border" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-yellow-500" onClick={() => execCommand("hiliteColor", "#fef08a")} title="Highlight" data-write-action>
                  <Highlighter className="h-4 w-4" />
                </Button>

                <Separator orientation="vertical" className="h-6 mx-1" />

                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => execCommand("justifyLeft")} title="Align Left" data-write-action>
                  <AlignLeft className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => execCommand("justifyCenter")} title="Align Center" data-write-action>
                  <AlignCenter className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => execCommand("justifyRight")} title="Align Right" data-write-action>
                  <AlignRight className="h-4 w-4" />
                </Button>

                <Separator orientation="vertical" className="h-6 mx-1" />

                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => execCommand("insertUnorderedList")} title="Bullet List" data-write-action>
                  <List className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => execCommand("insertOrderedList")} title="Numbered List" data-write-action>
                  <ListOrdered className="h-4 w-4" />
                </Button>

                <Separator orientation="vertical" className="h-6 mx-1" />

                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => {
                    const url = prompt("Enter link URL:");
                    if (url) execCommand("createLink", url);
                  }}
                  title="Link"
                >
                  <LinkIcon className="h-4 w-4" />
                </Button>
              </div>

              <div
                ref={setEditorRef}
                className="flex-1 p-6 overflow-y-auto outline-none prose max-w-none"
                contentEditable
                onInput={handleInput}
                suppressContentEditableWarning
                data-write-action
                data-testid="note-content-editor"
              />

              <div className="bg-gray-50 border-t p-2 px-4 flex justify-end text-xs text-gray-500">
                <span data-testid="word-count">{wc} words</span>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div
          className="flex flex-col items-center justify-center flex-1 text-gray-400 gap-3 p-8"
          data-testid="no-note-selected"
        >
          <FileText className="h-10 w-10 opacity-30" />
          <p className="text-sm">
            {notes.length === 0
              ? "No notes yet."
              : "Select a note from the list."}
          </p>
          {MULTI_NOTE_CREATION_ENABLED && notes.length === 0 && (
            <Button
              variant="outline"
              size="sm"
              disabled={createNote.isPending}
              onClick={handleNewNote}
              data-testid="empty-new-note-button"
            >
              <Plus className="h-4 w-4 mr-1" />
              New note
            </Button>
          )}
        </div>
      )}
    </div>
  );

  // ── layout ────────────────────────────────────────────────────────────────

  return (
    <div className="flex h-full bg-white" data-testid="notes-page">
      <div className="hidden md:flex w-full h-full" data-testid="desktop-layout">
        {sidebar}
        {editorPanel}
      </div>
      <div className="flex md:hidden w-full h-full">
        {mobileView === "list" ? sidebar : editorPanel}
      </div>
    </div>
  );
}
